import {useFocusManagement} from '@aboutyou/a11y';
import {Slide} from '@aboutyou/transitions';
import {css, cx} from '@linaria/core';
import {styled} from '@linaria/react';
import React, {FC, useEffect, useState} from 'react';
import {
  mediaMinXXSBreakpoint,
  tabletAndUpLinaria,
} from '../../../constants/customMedia';
import {Z_INDEXES} from '../../../constants/layers';
import {useCallbackDelayed} from '../../../hooks/useCallbackDelayed/useCallbackDelayed';
import {useMatchesMediaQuery} from '../../../hooks/useMatchesMediaQuery/useMatchesMediaQuery';
import {Alert} from '../Alert/Alert';
import {
  defaultEntranceAnimationDuration,
  defaultExitAnimationDuration,
} from './constants';
import {useAutoHide} from './hooks/useAutoHide';
import {ToastProps} from './types';

/**
 * @deprecated only for internal use, please use ToastContext from @aboutyou/ui-components instead
 */
export const Toast: FC<ToastProps> = ({
  content,
  onAutoClose,
  onDismiss,
  showCloseButton,
  type,
  cta,
  'data-testid': dataTestid = 'toast',
}) => {
  const [isMounted, setIsMounted] = useState(true);
  const isTabletOrUp = useMatchesMediaQuery(
    `(min-width: ${mediaMinXXSBreakpoint}px)`,
  );
  const {focusManagementRefCallback} = useFocusManagement({
    isOpen: isMounted,
  });

  const {delayedCallback: delayedAutoClose, cancelCallback: cancelAutoClose} =
    useCallbackDelayed(() => onAutoClose?.(), defaultExitAnimationDuration);
  const {delayedCallback: delayedOnDismiss} = useCallbackDelayed(
    () => onDismiss?.(),
    defaultExitAnimationDuration,
  );

  useEffect(() => {
    return () => {
      // cancelling in case the close all toast hook is called
      cancelAutoClose();
    };
  }, []);

  const onAutoHide = () => {
    setIsMounted(false);
    delayedAutoClose();
  };

  const onClickCloseButton = () => {
    delayedOnDismiss();
    setIsMounted(false);
  };

  useAutoHide({
    content:
      typeof content === 'function' ? content(onClickCloseButton) : content,
    closeCallback: onAutoHide,
    isDisabled: showCloseButton,
  });

  const direction = isTabletOrUp ? 'left' : 'up';

  return (
    <Slide
      mounted={isMounted}
      duration={{
        mount: defaultEntranceAnimationDuration,
        unmount: defaultExitAnimationDuration,
      }}
      direction={direction}
      customTimingFunction="ease-in"
    >
      <Wrapper
        data-testid={`${dataTestid}-${type}`}
        className={cx(TOAST_DIRECTION[direction])}
      >
        <div ref={focusManagementRefCallback()}>
          <Alert
            type={type}
            content={content}
            cta={cta}
            showCloseButton={showCloseButton}
            onClickCloseButton={onClickCloseButton}
          />
        </div>
      </Wrapper>
    </Slide>
  );
};

const TOAST_DIRECTION = {
  up: css`
    bottom: 0;
    left: 0;
  `,
  left: css`
    top: 1rem;
    right: 1rem;
  `,
};

const Wrapper = styled.div`
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.3) 0%,
    rgba(0, 0, 0, 0) 100%
  );
  position: fixed;
  z-index: ${Z_INDEXES.TOAST_NOTIFICATION};
  width: 100%;
  padding: 0 0.5rem 0.5rem;

  ${tabletAndUpLinaria(`
    background: unset;
    padding: 0;
    max-width: 375px;
  `)}
`;
